import React, { useEffect } from "react";
import { useForm, FieldErrors } from "react-hook-form";
import { Form } from "reactstrap";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import { useRoleService } from "modules/Library/Role/api/useRoleQuery";
import {
  EditStage,
  Stage,
  StagePayload,
} from "../helpers/workflow.types";
import { validations } from "validations/FormValidations";

/* =====================================================
   Types
===================================================== */

interface StageFormValues {
  stageName: string;
  role: number | string | null;
  returnTo: number | string | null;
}

interface StageInputFormProps {
  onAddStage: (payload: StagePayload) => void;
  onUpdateStage: (payload: EditStage) => void;
  editStage?: EditStage | null;
  stages?: Stage[];
}

/* =====================================================
   Component
===================================================== */

const StageInputForm: React.FC<StageInputFormProps> = ({
  onAddStage,
  editStage,
  onUpdateStage,
  stages = [],
}) => {
  const { DropdownQuery } = useRoleService({ getDropdown: true });
  const { data: rolesList } = DropdownQuery;

  /* -----------------------------
     react-hook-form setup
  ----------------------------- */
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<StageFormValues>({
    defaultValues: {
      stageName: "",
      role: null,
      returnTo: null,
    },
    mode: "onChange",
  });

  /* -----------------------------
     Load edit data
  ----------------------------- */
  useEffect(() => {
    if (!editStage) {
      reset({ stageName: "", role: null, returnTo: null });
      return;
    }
    if (!rolesList?.data?.length) return;

    reset({
      stageName: editStage.stageName?.trim() || "",
      role: editStage.role ?? null,
      returnTo: editStage.returnTo ?? null,
    });
  }, [editStage, rolesList, reset]);

  /* -----------------------------
     Options
  ----------------------------- */
  const roleOptions = (rolesList?.data || []).map((r: any) => ({
    value: r.roleid,
    label: r.title,
  }));

  const returnToOptions = stages
    .filter(
      (stage) =>
        !stage.isDeleted &&
        stage.stageName !== "Not Started" &&
        stage.stageId !== editStage?.stageId,
    )
    .map((s) => ({
      value: s.stageId,
      label: s.stageName,
    }));

  /* -----------------------------
     Field config
  ----------------------------- */
  const stageFormFields: FormFieldConfig[] = [
    {
      name: "stageName",
      label: "Stage Name",
      type: "text",
      placeholder: "Enter stage name",
      required: true,
      rules: {
        validate: (value: string) => {
          const result = validations.workflow.stagename.validate(value);
          return result === true ? true : result;
        },
      },
    },
    {
      name: "role",
      label: "Role",
      type: "select",
      placeholder: "Select role",
      required: true,
      options: roleOptions,
      isClearable: false,
    },
    {
      name: "returnTo",
      label: "Return To",
      type: "select",
      placeholder: "Select return stage",
      required: false,
      options: returnToOptions,
      isClearable: true,
    },
  ];

  /* -----------------------------
     Submit
  ----------------------------- */
  const onSubmit = (values: StageFormValues) => {
    const selectedRole = roleOptions.find((r:any) => r.value === values.role);
    const selectedReturnTo = returnToOptions.find((s) => s.value === values.returnTo);

    const payload: StagePayload = {
      stageName: values.stageName,
      role: values.role as number | null,
      roleName: selectedRole?.label || "",
      returnTo: (selectedReturnTo?.value as number) ?? null,
    };

    if (editStage) {
      onUpdateStage({
        ...payload,
        stageId: editStage.stageId,
      });
    } else {
      onAddStage(payload);
    }
  };

  /* =====================================================
     Render
  ===================================================== */

  return (
    <Form onSubmit={handleSubmit(onSubmit)} id="stage-form-submit">
      <CommonForm
        fields={stageFormFields}
        control={control}
        errors={errors as FieldErrors}
        columns={1}
        setValue={setValue}
        getValues={getValues}
      />
    </Form>
  );
};

export default StageInputForm;
